import axios from 'axios';
import {
  clearAdminSessionTokens,
  getAdminAccessToken,
  getAdminRefreshToken,
  hasAdminSessionTimedOutByInactivity,
  isAdminSessionExtending,
  isAdminWarningGraceActive,
  isAdminSessionRefreshDue,
  markAdminInteraction,
  markAdminSessionRefreshed,
  markAdminSessionTimedOut,
  setAdminRedirectPath,
  setAdminSessionTokens,
} from './adminSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api/admin`,
});

const isAuthRoute = (url = '') => String(url).includes('/auth/');

api.interceptors.request.use((config) => {
  if (!isAuthRoute(config.url) && hasAdminSessionTimedOutByInactivity() && !isAdminWarningGraceActive() && !isAdminSessionExtending()) {
    clearAdminSessionTokens();
    notifyAdminSessionExpired();
    const idleError = new Error('Session expired due to inactivity');
    idleError.response = {
      status: 401,
      data: { detail: 'Session expired due to inactivity' },
    };
    throw idleError;
  }
  if (!isAuthRoute(config.url) && isAdminSessionRefreshDue()) {
    const refreshToken = getAdminRefreshToken();
    if (refreshToken) {
      if (!refreshPromise) {
        refreshPromise = axios.post(`${API_URL}/api/admin/auth/refresh`, { refresh_token: refreshToken });
      }
      return refreshPromise.then((refreshResponse) => {
        const { access_token, refresh_token } = refreshResponse.data;
        setAdminSessionTokens({ accessToken: access_token, refreshToken: refresh_token });
        markAdminSessionRefreshed();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${access_token}`;
        return config;
      }).catch((error) => {
        clearAdminSessionTokens();
        notifyAdminSessionExpired();
        throw error;
      }).finally(() => {
        refreshPromise = null;
      });
    }
  }
  const token = getAdminAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise = null;

const notifyAdminSessionExpired = () => {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (currentPath && currentPath !== '/login') {
    setAdminRedirectPath(currentPath);
  }
  markAdminSessionTimedOut();
  window.dispatchEvent(new CustomEvent('admin-session-expired'));
};

api.interceptors.response.use(
  (response) => {
    if (!isAuthRoute(response.config?.url)) {
      markAdminInteraction();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const refreshToken = getAdminRefreshToken();

    if (error.response?.status === 401 && refreshToken && !originalRequest._retry && !String(originalRequest.url || '').includes('/auth/refresh')) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API_URL}/api/admin/auth/refresh`, { refresh_token: refreshToken });
        }
        const refreshResponse = await refreshPromise;
        const { access_token, refresh_token } = refreshResponse.data;
        setAdminSessionTokens({ accessToken: access_token, refreshToken: refresh_token });
        markAdminSessionRefreshed();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        if (!isAdminSessionExtending()) {
          clearAdminSessionTokens();
          notifyAdminSessionExpired();
        }
        return Promise.reject(refreshError);
      } finally {
        refreshPromise = null;
      }
    }

    if (error.response?.status === 401 && !isAdminSessionExtending()) {
      clearAdminSessionTokens();
      notifyAdminSessionExpired();
    }
    return Promise.reject(error);
  }
);

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const adminApi = {
  reauthenticateAdmin: (data) => api.post('/auth/reauth', data),

  // Dashboard
  getDashboard: (params) => api.get('/dashboard', { params }),
  
  // Sales Staff
  getSalesStaff: (params) => api.get('/sales-staff', { params }),
  getSalesStaffById: (id) => api.get(`/sales-staff/${id}`),
  createSalesStaff: (data) => api.post('/sales-staff', data),
  updateSalesStaff: (id, data) => api.put(`/sales-staff/${id}`, data),
  toggleSalesStaffStatus: (id) => api.patch(`/sales-staff/${id}/toggle-status`),
  
  // Customers
  getCustomers: (params) => api.get('/customers', { params }),
  createCustomer: (data) => api.post('/customers', data),
  getCustomerById: (id) => api.get(`/customers/${id}`),
  updateCustomer: (id, data) => api.put(`/customers/${id}`, data),
  getCustomerPricing: (id) => api.get(`/customers/${id}/pricing`),
  updateCustomerPricing: (id, data) => api.put(`/customers/${id}/pricing`, data),
  
  // Products
  getProducts: (params) => api.get('/products', { params }),
  createProduct: (data) => api.post('/products', data),
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  toggleProductStatus: (id) => api.patch(`/products/${id}/toggle-status`),
  deleteProduct: (id) => api.delete(`/products/${id}`),

  // Product Masters
  getProductMasters: (params) => api.get('/masters/products', { params }),
  createProductMaster: (data) => api.post('/masters/products', data),
  updateProductMaster: (id, data) => api.put(`/masters/products/${id}`, data),
  backfillProductMasterHsnCodes: (apply = false) => api.post('/masters/products/hsn/backfill', null, { params: { apply } }),

  // Pack Sizes
  getPackSizes: (params) => api.get('/masters/pack-sizes', { params }),
  createPackSize: (data) => api.post('/masters/pack-sizes', data),
  updatePackSize: (id, data) => api.put(`/masters/pack-sizes/${id}`, data),

  // Product Variants
  getProductVariants: (params) => api.get('/product-variants', { params }),
  createProductVariant: (data) => api.post('/product-variants', data),
  updateProductVariant: (id, data) => api.put(`/product-variants/${id}`, data),
  getVariantBlendRecipe: (id) => api.get(`/product-variants/${id}/blend-recipe`),
  updateVariantBlendRecipe: (id, data) => api.put(`/product-variants/${id}/blend-recipe`, data),
  uploadVariantImage: (id, file, isPrimary = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_primary', isPrimary ? 'true' : 'false');
    return api.post(`/product-variants/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteVariantImage: (variantId, imageId) => api.delete(`/product-variants/${variantId}/images/${imageId}`),
  setVariantPrimaryImage: (variantId, imageId) => api.patch(`/product-variants/${variantId}/images/${imageId}/primary`),
  uploadVariantStickerLabel: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/product-variants/${id}/sticker-label`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteVariantStickerLabel: (id) => api.delete(`/product-variants/${id}/sticker-label`),
  getVariantPricingModule: (id) => api.get(`/product-variants/${id}/pricing-module`),
  updateVariantCosting: (id, data) => api.put(`/product-variants/${id}/costing`, data),
  updateVariantPricing: (id, data) => api.put(`/product-variants/${id}/pricing`, data),
  updateVariantPriceRules: (id, data) => api.put(`/product-variants/${id}/price-rules`, data),
  updateVariantDiscountSlabs: (id, data) => api.put(`/product-variants/${id}/discount-slabs`, data),
  getVariantPriceHistory: (id, params) => api.get(`/product-variants/${id}/price-history`, { params }),
  
  // Orders
  getOrders: (params) => api.get('/orders', { params }),
  getOrderById: (id) => api.get(`/orders/${id}`),
  createOrder: (data) => api.post('/orders', data),
  updateOrderStatus: (id, data) => api.patch(`/orders/${id}/status`, data),
  generateBillOfSupply: (id) => api.post(`/orders/${id}/bill-of-supply`),
  downloadBillOfSupplyFile: (id, params = {}) => api.get(`/orders/${id}/bill-of-supply/download`, { params, responseType: 'blob' }),
  bulkDownloadBillOfSupply: (order_ids) => api.post('/orders/bill-of-supply/bulk-download', { order_ids }, { responseType: 'blob' }),
  
  // Payments
  getPayments: (params) => api.get('/payments', { params }),
  createPayment: (data) => api.post('/payments', data),
  getStaffCollectionSummary: () => api.get('/staff-collections/summary'),
  getPendingStaffCollections: (params) => api.get('/staff-collections/pending', { params }),
  getStaffCollectionSettlements: (params) => api.get('/staff-collections/settlements', { params }),
  createStaffCollectionSettlement: (data) => api.post('/staff-collections/settlements', data),
  getFinancialYearConfig: () => api.get('/financial-years/config'),
  getFinancialYearControl: () => api.get('/financial-years/control'),
  startFinancialYearFreeze: (params = {}) => api.post('/financial-years/freeze/start', null, { params }),
  releaseFinancialYearFreeze: () => api.post('/financial-years/freeze/release'),
  markFinancialYearBackupComplete: () => api.post('/financial-years/backup/mark-complete'),
  getFinancialYearClosures: () => api.get('/financial-years/closures'),
  getFinancialYearClosureById: (id, params = {}) => api.get(`/financial-years/closures/${id}`, { params }),
  previewFinancialYearClosure: (data) => api.post('/financial-years/closures/preview', data),
  updateFinancialYearSignoff: (id, data) => api.put(`/financial-years/closures/${id}/signoff`, data),
  confirmFinancialYearClosure: (id, data = {}) => api.post(`/financial-years/closures/${id}/confirm`, data),
  previewFinancialYearOpenings: (id) => api.post(`/financial-years/closures/${id}/openings/preview`),
  postFinancialYearOpenings: (id, data = {}) => api.post(`/financial-years/closures/${id}/openings/post`, data),
  getFinancialYearLeavePolicies: () => api.get('/financial-years/leave-policies'),
  updateFinancialYearLeavePolicies: (data) => api.put('/financial-years/leave-policies', data),
  updateFinancialYearLeaveSeeds: (id, data) => api.put(`/financial-years/closures/${id}/leave-seeds`, data),
  updateFinancialYearInventorySeeds: (id, data) => api.put(`/financial-years/closures/${id}/inventory-seeds`, data),
  createFinancialYearCorrection: (id, data) => api.post(`/financial-years/closures/${id}/corrections`, data),
  downloadFinancialYearSectionExcel: (id, section) => api.get(`/financial-years/closures/${id}/exports/${section}`, { responseType: 'blob' }),
  downloadFinancialYearClosingPack: (id) => api.get(`/financial-years/closures/${id}/closing-pack`, { responseType: 'blob' }),
  downloadAdminDatasetExcel: (dataset, params = {}) => api.get(`/exports/xlsx/${dataset}`, { params, responseType: 'blob' }),
  getSecurityAuditLogs: (params = {}) => api.get('/security/audit-logs', { params }),
  
  // Leads
  getLeads: (params) => api.get('/leads', { params }),
  getLeadById: (id) => api.get(`/leads/${id}`),
  updateLead: (id, data) => api.put(`/leads/${id}`, data),
  addLeadAction: (id, data) => api.post(`/leads/${id}/actions`, data),
  getLeadTasks: (params) => api.get('/leads/tasks', { params }),
  deleteLead: (id) => api.delete(`/leads/${id}`),
  createLeadManual: (data) => api.post('/leads/manual', data),

  // CRM
  getCrmRecords: (params) => api.get('/crm', { params }),
  getCrmRecord: (entityType, id) => api.get(`/crm/${entityType}/${id}`),
  convertLeadToCustomer: (id, data) => api.post(`/crm/leads/${id}/convert`, data),
  sendCrmWhatsapp: (entityType, id, data) => api.post(`/crm/${entityType}/${id}/whatsapp`, data),

  // Tasks (Admin created)
  getTasks: (params) => api.get('/tasks', { params }),
  createTask: (data) => api.post('/tasks', data),
  completeTask: (id, data) => api.patch(`/tasks/${id}/complete`, data),
  sendTaskReminder: (id) => api.post(`/tasks/${id}/remind`),

  // Staff Types
  getStaffTypes: () => api.get('/staff-types'),
  createStaffType: (data) => api.post('/staff-types', data),
  updateStaffType: (id, data) => api.put(`/staff-types/${id}`, data),
  deleteStaffType: (id) => api.delete(`/staff-types/${id}`),

  // Staff Activities
  getStaffActivities: (params) => api.get('/staff-activities', { params }),
  createStaffActivity: (data) => api.post('/staff-activities', data),

  // Payroll
  getPayrollStaff: () => api.get('/payroll/staff'),
  updatePayrollStaffSettings: (staffId, data) => api.put(`/payroll/staff/${staffId}/salary-settings`, data),
  getPayrollAttendance: (params) => api.get('/payroll/attendance', { params }),
  markPayrollAttendance: (data) => api.post('/payroll/attendance/mark', data),
  getPayrollLeaves: (params) => api.get('/payroll/leaves', { params }),
  createPayrollLeave: (data) => api.post('/payroll/leaves', data),
  approvePayrollLeave: (id, data) => api.patch(`/payroll/leaves/${id}/approve`, data),
  rejectPayrollLeave: (id, data) => api.patch(`/payroll/leaves/${id}/reject`, data),
  getPayrollHolidays: (params) => api.get('/payroll/holidays', { params }),
  createPayrollHoliday: (data) => api.post('/payroll/holidays', data),
  updatePayrollHoliday: (id, data) => api.put(`/payroll/holidays/${id}`, data),
  deletePayrollHoliday: (id) => api.delete(`/payroll/holidays/${id}`),
  generatePayrollSlips: (data) => api.post('/payroll/slips/generate', data),
  getPayrollSlips: (params) => api.get('/payroll/slips', { params }),
  getPayrollSlipById: (id) => api.get(`/payroll/slips/${id}`),
  getIncentiveRules: () => api.get('/payroll/incentive-rules'),
  saveIncentiveRuleDraft: (data) => api.put('/payroll/incentive-rules/draft', data),
  simulateIncentiveRule: (data) => api.post('/payroll/incentive-rules/simulate', data),
  publishIncentiveRule: (data = {}) => api.post('/payroll/incentive-rules/publish', data),

  // Expenses
  getExpenseCategories: (params) => api.get('/expenses/categories', { params }),
  createExpenseCategory: (data) => api.post('/expenses/categories', data),
  updateExpenseCategory: (id, data) => api.put(`/expenses/categories/${id}`, data),
  deleteExpenseCategory: (id) => api.delete(`/expenses/categories/${id}`),
  getExpenses: (params) => api.get('/expenses', { params }),
  createExpense: (data) => api.post('/expenses', data),
  updateExpense: (id, data) => api.put(`/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/expenses/${id}`),
  
  // Reports
  getSalesSummary: (period) => api.get('/reports/sales-summary', { params: { period } }),
  getStaffPerformance: (period, params = {}) => api.get('/reports/staff-performance', { params: { period, ...params } }),
  getProductSales: (period, params = {}) => api.get('/reports/product-sales', { params: { period, ...params } }),
  getCollectionSummary: (period, params = {}) => api.get('/reports/collection-summary', { params: { period, ...params } }),
  getFinancialSummary: (period) => api.get('/reports/financial-summary', { params: { period } }),
  getInventoryForecast: (params) => api.get('/reports/inventory-forecast', { params }),
  getAlertingHub: (params) => api.get('/alerts/hub', { params }),
  updateAlertingStatus: (data) => api.post('/alerts/hub/action', data),

  // Warehouse
  getWarehouses: () => api.get('/warehouses'),
  createWarehouse: (data) => api.post('/warehouses', data),
  updateWarehouse: (id, data) => api.put(`/warehouses/${id}`, data),
  getWarehouseStock: (warehouseId) => api.get(`/warehouses/${warehouseId}/stock`),
  adjustWarehouseStock: (warehouseId, data) => api.post(`/warehouses/${warehouseId}/adjust`, data),
  transferStock: (data) => api.post('/warehouses/transfer', data),
  getStockMovements: (params) => api.get('/stock/movements', { params }),
  updateStockMovement: (id, data) => api.patch(`/stock/movements/${id}`, data),

  // Raw Items & Raw Stock
  getRawItems: () => api.get('/raw-items'),
  createRawItem: (data) => api.post('/raw-items', data),
  updateRawItem: (id, data) => api.put(`/raw-items/${id}`, data),
  getRawStockLots: (params) => api.get('/raw-stock/lots', { params }),
  getNextRawStockRef: () => api.get('/raw-stock/next-ref'),
  receiveRawStock: (data) => api.post('/raw-stock/receive', data),
  suggestRawStock: (params) => api.get('/raw-stock/suggest', { params }),
  transferRawStock: (data) => api.post('/raw-stock/transfer', data),
  adjustRawStock: (data) => api.post('/raw-stock/adjust', data),
  getRawStockMovements: (params) => api.get('/raw-stock/movements', { params }),
  getRawStockDaywiseAudit: (params) => api.get('/raw-stock/daywise-audit', { params }),

  // Bulk blending
  getBlendItems: () => api.get('/blend-items'),
  createBlendItem: (data) => api.post('/blend-items', data),
  updateBlendItem: (id, data) => api.put(`/blend-items/${id}`, data),
  getBlendItemRecipe: (id) => api.get(`/blend-items/${id}/recipe`),
  updateBlendItemRecipe: (id, data) => api.put(`/blend-items/${id}/recipe`, data),
  getBlendSessions: (params) => api.get('/blend-sessions', { params }),
  createBlendSession: (data) => api.post('/blend-sessions', data),
  updateBlendSession: (id, data) => api.put(`/blend-sessions/${id}`, data),
  previewBlendSessionStockPreparation: (id) => api.post(`/blend-sessions/${id}/prepare-stock/preview`),
  confirmBlendSessionStockPreparation: (id) => api.post(`/blend-sessions/${id}/prepare-stock/confirm`),
  startBlendSession: (id) => api.post(`/blend-sessions/${id}/start`),
  completeBlendSession: (id, data) => api.post(`/blend-sessions/${id}/complete`, data),
  cancelBlendSession: (id, data = {}) => api.post(`/blend-sessions/${id}/cancel`, data),
  getBlendedStockLots: (params) => api.get('/blended-stock/lots', { params }),
  getBlendedStockMovements: (params) => api.get('/blended-stock/movements', { params }),
  getPackingMaterialItems: () => api.get('/packing-material-items'),
  createPackingMaterialItem: (data) => api.post('/packing-material-items', data),
  updatePackingMaterialItem: (id, data) => api.put(`/packing-material-items/${id}`, data),
  getPackingMaterialStockLots: (params) => api.get('/packing-material-stock/lots', { params }),
  receivePackingMaterialStock: (data) => api.post('/packing-material-stock/receive', data),
  transferPackingMaterialStock: (data) => api.post('/packing-material-stock/transfer', data),
  getPackingMaterialStockMovements: (params) => api.get('/packing-material-stock/movements', { params }),

  // Packing Requests
  getPackingRequests: (params) => api.get('/packing-requests', { params }),
  deletePackingRequest: (id) => api.delete(`/packing-requests/${id}`),
  completePackingRequest: (id, data) => api.post(`/packing-requests/${id}/complete`, data),
  getPackingSessions: (params) => api.get('/packing-sessions', { params }),
  getPackingSessionById: (id) => api.get(`/packing-sessions/${id}`),
  createPackingSession: (data) => api.post('/packing-sessions', data),
  updatePackingSession: (id, data) => api.put(`/packing-sessions/${id}`, data),
  previewPackingSessionStockPreparation: (id) => api.post(`/packing-sessions/${id}/prepare-stock/preview`),
  confirmPackingSessionStockPreparation: (id) => api.post(`/packing-sessions/${id}/prepare-stock/confirm`),
  startPackingSession: (id) => api.post(`/packing-sessions/${id}/start`),
  completePackingSession: (id, data) => api.post(`/packing-sessions/${id}/complete`, data),
  cancelPackingSession: (id, data = {}) => api.post(`/packing-sessions/${id}/cancel`, data),
  suggestPackingSessionRaw: (data) => api.post('/packing-sessions/suggest-raw', data),
  previewPackingSessionStickers: (id) => api.get(`/packing-sessions/${id}/stickers/preview`),
  generatePackingSessionStickers: (id) => api.post(`/packing-sessions/${id}/stickers`),
  downloadPackingSessionStickers: (id) => api.get(`/packing-sessions/${id}/stickers/download`, { responseType: 'blob' }),

  // Vendors & Purchase Orders
  getVendors: () => api.get('/vendors'),
  createVendor: (data) => api.post('/vendors', data),
  updateVendor: (id, data) => api.put(`/vendors/${id}`, data),
  getVendorLedger: (id) => api.get(`/vendors/${id}/ledger`),

  getPurchaseOrders: (params) => api.get('/purchase-orders', { params }),
  createPurchaseOrder: (data) => api.post('/purchase-orders', data),
  getPurchaseOrderById: (id) => api.get(`/purchase-orders/${id}`),
  updatePurchaseOrderStatus: (id, data) => api.patch(`/purchase-orders/${id}/status`, data),
  receivePurchaseOrder: (id, data) => api.post(`/purchase-orders/${id}/receive`, data),
  getPurchaseOrderReceipts: (id) => api.get(`/purchase-orders/${id}/receipts`),
  createPurchaseOrderPaymentVoucher: (id, data = {}) => api.post(`/purchase-orders/${id}/create-payment-voucher`, data),

  getVendorPayments: (params) => api.get('/vendor-payments', { params }),
  createVendorPayment: (data) => api.post('/vendor-payments', data),
  
  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
  getReminderSettings: () => api.get('/reminders/settings'),
  updateReminderSettings: (data) => api.put('/reminders/settings', data),
  getWhatsappSummarySettings: () => api.get('/whatsapp/summary-settings'),
  updateWhatsappSummarySettings: (data) => api.put('/whatsapp/summary-settings', data),
  previewWhatsappDailySummary: () => api.get('/whatsapp/daily-summary/preview'),
  sendWhatsappDailySummary: () => api.post('/whatsapp/daily-summary/send'),
  getEmailTemplates: () => api.get('/email-templates'),
  createEmailTemplate: (data) => api.post('/email-templates', data),
  updateEmailTemplate: (key, data) => api.put(`/email-templates/${key}`, data),
  previewEmailTemplate: (key, data) => api.post(`/email-templates/${key}/preview`, data),
  deleteEmailTemplate: (key) => api.delete(`/email-templates/${key}`),
  getReturnReasons: () => api.get('/settings/return-reasons'),
  createReturnReason: (data) => api.post('/settings/return-reasons', data),
  updateReturnReason: (id, data) => api.put(`/settings/return-reasons/${id}`, data),
  deleteReturnReason: (id) => api.delete(`/settings/return-reasons/${id}`),
  getAreaMasters: () => api.get('/settings/areas'),
  createAreaMaster: (data) => api.post('/settings/areas', data),
  updateAreaMaster: (id, data) => api.put(`/settings/areas/${id}`, data),
  deleteAreaMaster: (id) => api.delete(`/settings/areas/${id}`),
  getMobileReleaseSettings: () => api.get('/settings/mobile-release'),
  prepareMobileReleaseApkUpload: (data) => api.post('/settings/mobile-release/apk/prepare', data),
  uploadMobileReleaseApk: (file, metadata = {}, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('latest_version', metadata.latest_version || '');
    formData.append('latest_build_number', String(metadata.latest_build_number || ''));
    return api.post('/settings/mobile-release/apk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
  updateMobileReleaseSettings: (data) => api.put('/settings/mobile-release', data),

  // Short URLs
  getShortUrls: (params) => api.get('/short-urls', { params }),
  createShortUrl: (data) => api.post('/short-urls', data),
  updateShortUrl: (id, data) => api.put(`/short-urls/${id}`, data),

  // QR Codes
  getQrCodes: (params) => api.get('/qr-codes', { params }),
  createQrCode: (data) => api.post('/qr-codes', data),
  
  // Testimonials
  getTestimonials: (params) => api.get('/testimonials', { params }),
  updateTestimonial: (id, data) => api.patch(`/testimonials/${id}`, data),

  // Finance
  getAccounts: () => api.get('/finance/accounts'),
  createAccount: (data) => api.post('/finance/accounts', data),
  seedAccounts: () => api.post('/finance/accounts/seed'),

  getVouchers: () => api.get('/finance/vouchers'),
  createVoucher: (data) => api.post('/finance/vouchers', data),
  postVoucher: (id) => api.post(`/finance/vouchers/${id}/post`),
  getVoucherPdfUrl: (id, params) => api.get(`/finance/vouchers/${id}/pdf-url`, { params }),

  getCreditNotes: () => api.get('/finance/credit-notes'),
  getRedeemableCreditNotes: (customerId) => api.get('/finance/credit-notes/redeemable', { params: { customer_id: customerId } }),
  createCreditNote: (data) => api.post('/finance/credit-notes', data),
  postCreditNote: (id) => api.post(`/finance/credit-notes/${id}/post`),

  getSalesReturns: () => api.get('/sales-returns'),
  createSalesReturn: (data) => api.post('/sales-returns', data),

  getLedger: (params) => api.get('/finance/ledger', { params }),
  generateGstInvoice: (id) => api.post(`/orders/${id}/gst-invoice`),
  downloadGstInvoiceFile: (id) => api.get(`/orders/${id}/gst-invoice/download`, { responseType: 'blob' }),
  getGstSummary: (params) => api.get('/gst/summary', { params }),
  getGstDeposits: () => api.get('/gst/deposits'),
  createGstDeposit: (data) => api.post('/gst/deposits', data),

  // Reports
  getShortUrlReport: (period) => api.get('/reports/short-urls', { params: { period } }),
  getQrCodeReport: (period) => api.get('/reports/qr-codes', { params: { period } }),
  
  // Admin Users
  getAdminUsers: () => api.get('/users'),
  createAdminUser: (data) => api.post('/users', data),
  updateAdminUser: (id, data) => api.put(`/users/${id}`, data),
  deleteAdminUser: (id) => api.delete(`/users/${id}`),
  getAdminMfaStatus: () => api.get('/auth/mfa/status'),
  setupAdminMfa: () => api.post('/auth/mfa/setup'),
  verifyAdminMfa: (data) => api.post('/auth/mfa/verify', data),
  disableAdminMfa: (data) => api.post('/auth/mfa/disable', data),

  // Push Notifications
  testNotification: (data) => api.post('/push/test', data),
  getPushMeta: () => api.get('/push/meta'),
  estimatePushAudience: (data) => api.post('/push/estimate-audience', data),
  getPushCampaigns: (params) => api.get('/push/campaigns', { params }),
  getPushCampaignById: (id) => api.get(`/push/campaigns/${id}`),
  createPushCampaign: (data) => api.post('/push/campaigns', data),
  duplicatePushCampaign: (id) => api.post(`/push/campaigns/${id}/duplicate`),
  sendPushCampaign: (id) => api.post(`/push/campaigns/${id}/send`),

  // Database Administration
  getDbCollections: () => api.get('/db/collections'),
  backupDatabase: () => api.post('/db/backup'),
  getDbBackups: (params) => api.get('/db/backups', { params }),
  restoreBackup: (data) => api.post('/db/restore-backup', data),
  getDbAutomationStatus: () => api.get('/db/automation/status'),
  runDbAutomation: () => api.post('/db/automation/run'),
  resetDatabase: (data) => api.post('/db/reset', data),
  resetCounters: (data) => api.post('/db/reset-counters', data),
  
  // Meta
  getAreas: () => api.get('/meta/areas'),
  getLeadStatuses: () => api.get('/meta/lead-statuses'),
  getQuickStats: () => api.get('/meta/stats'),
  getRoles: () => api.get('/meta/roles'),
};

export default api;
